<template>
  <div>
    <b-row class="mb-2">
      <b-col md="4">
        <div class="emp-stat">
          <div class="emp-stat__icon">
            <feather-icon
              icon="BriefcaseIcon"
              size="22"
            />
          </div>
          <div>
            <small>Staff</small>
            <h4 class="mb-0">{{ stats.staff }}</h4>
            <div class="emp-stat__hint">Union employees</div>
          </div>
        </div>
      </b-col>
      <b-col md="4">
        <div class="emp-stat">
          <div class="emp-stat__icon">
            <feather-icon
              icon="AwardIcon"
              size="22"
            />
          </div>
          <div>
            <small>Union executives</small>
            <h4 class="mb-0">{{ stats.union_executives }}</h4>
            <div class="emp-stat__hint">Elected or appointed leaders</div>
          </div>
        </div>
      </b-col>
      <b-col md="4">
        <div class="emp-stat">
          <div class="emp-stat__icon">
            <feather-icon
              icon="CheckCircleIcon"
              size="22"
            />
          </div>
          <div>
            <small>Active records</small>
            <h4 class="mb-0">{{ stats.active_records }}</h4>
            <div class="emp-stat__hint">Active staff and serving executives</div>
          </div>
        </div>
      </b-col>
    </b-row>

    <b-card>
      <div class="employees-toolbar">
        <div>
          <h3 class="employees-toolbar__title mb-25">
            Employee Centre
          </h3>
          <div class="text-muted">
            Manage union staff and elected or appointed executive records.
          </div>
        </div>
        <div class="employees-toolbar__actions">
          <b-form-select
            v-model="typeFilter"
            :options="typeFilterOptions"
            class="employees-toolbar__filter"
            @change="load"
          />
          <b-form-input
            v-model="search"
            placeholder="Search name, role or ID..."
            class="employees-toolbar__search"
            @keyup.enter="load"
          />
          <am-button
            class="employees-toolbar__add"
            icon="PlusIcon"
            label="Add person"
            @click="openSetup()"
          />
        </div>
      </div>

      <div
        v-if="loading"
        class="text-center py-5"
      >
        <b-spinner variant="primary" />
      </div>

      <div
        v-else-if="!employees.length"
        class="employees-empty text-center py-5"
      >
        <div class="employees-empty__icon">
          <feather-icon
            icon="UsersIcon"
            size="36"
          />
        </div>
        <h4 class="mb-50">
          No employees yet
        </h4>
        <p class="text-muted mb-2">
          {{ emptyMessage }}
        </p>
        <am-button
          v-if="!hasFilters"
          class="employees-toolbar__add"
          icon="PlusIcon"
          label="Add person"
          @click="openSetup()"
        />
        <am-button
          v-else
          variant="outline-primary"
          label="Clear filters"
          @click="clearFilters"
        />
      </div>

      <b-row v-else>
        <b-col
          v-for="employee in employees"
          :key="employee.id"
          md="4"
          class="mb-2"
        >
          <article
            class="person-card h-100 cursor-pointer"
            @click="$router.push({ name: 'employee-detail', params: { id: employee.id } })"
          >
            <div class="person-card__head">
              <div class="person-card__avatar">
                {{ initials(employee) }}
              </div>
              <div class="person-card__identity">
                <b>{{ employee.full_name }}</b>
                <div class="person-card__role">
                  {{ employee.position }}
                </div>
              </div>
              <span
                class="person-card__badge"
                :class="employee.employee_type === 'staff' ? 'is-staff' : 'is-executive'"
              >
                {{ employee.employee_type === 'staff' ? 'Staff' : 'Union Executive' }}
              </span>
            </div>

            <div class="person-card__meta">
              <div>
                <small>{{ employee.employee_type === 'staff' ? 'Employee ID' : 'Executive ID' }}</small>
                <b>{{ employee.employee_code || '—' }}</b>
              </div>
              <div>
                <small>Mobile</small>
                <b>{{ employee.mobile || '—' }}</b>
              </div>
              <div>
                <small>{{ employee.employee_type === 'staff' ? 'Department' : 'Committee' }}</small>
                <b>
                  {{ employee.employee_type === 'staff'
                    ? (employee.department ? employee.department.name : 'Not assigned')
                    : (employee.committee || 'Not assigned') }}
                </b>
              </div>
              <div>
                <small>{{ employee.employee_type === 'staff' ? 'Date employed' : 'Term period' }}</small>
                <b>{{ employeeMetaDate(employee) }}</b>
              </div>
              <div>
                <small>Status</small>
                <b-badge
                  class="person-card__status-badge"
                  :variant="statusBadgeVariant(employeeStatus(employee))"
                >
                  {{ employeeStatus(employee) }}
                </b-badge>
              </div>
            </div>
          </article>
        </b-col>
      </b-row>
    </b-card>

    <employee-setup
      :modal-opened="setupOpened"
      :employee="editing"
      @modalClosed="setupOpened = false"
      @saved="onSaved"
    />
  </div>
</template>

<script>
import {
  BRow, BCol, BCard, BFormSelect, BFormInput, BSpinner, BBadge,
} from 'bootstrap-vue'
import AmButton from '@/farmbusiness/components/AmButton.vue'
import EmployeeSetup from '@/views/employee-centre/employees/EmployeeSetup.vue'
import showToast from '@/farmbusiness/mixins/showToast'

export default {
  name: 'EmployeesIndex',
  components: {
    AmButton, EmployeeSetup, BRow, BCol, BCard, BFormSelect, BFormInput, BSpinner, BBadge,
  },
  mixins: [showToast],
  data() {
    return {
      loading: false,
      search: '',
      typeFilter: '',
      setupOpened: false,
      editing: null,
      typeFilterOptions: [
        { value: '', text: 'All types' },
        { value: 'staff', text: 'Staff' },
        { value: 'union_executive', text: 'Union Executive' },
      ],
    }
  },
  computed: {
    employees() {
      return this.$store.getters['employeeCentre/employees']
    },
    stats() {
      return this.$store.getters['employeeCentre/stats']
    },
    hasFilters() {
      return !!(this.search || this.typeFilter)
    },
    emptyMessage() {
      if (this.hasFilters) {
        return 'No employees match your current filters.'
      }
      return 'Register staff or union executives to get started.'
    },
  },
  mounted() {
    this.$store.dispatch('employeeCentre/fetchDepartments')
    this.load()
  },
  methods: {
    load() {
      this.loading = true
      this.$store.dispatch('employeeCentre/fetchEmployees', {
        search: this.search,
        employee_type: this.typeFilter,
      }).finally(() => { this.loading = false })
    },
    clearFilters() {
      this.search = ''
      this.typeFilter = ''
      this.load()
    },
    openSetup(employee = null) {
      this.editing = employee
      this.setupOpened = true
    },
    onSaved() {
      this.setupOpened = false
      this.load()
    },
    initials(employee) {
      const first = employee.first_name ? employee.first_name[0] : ''
      const last = employee.last_name ? employee.last_name[0] : ''
      return `${first}${last}`.toUpperCase() || '?'
    },
    formatDate(value) {
      if (!value) return '—'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return value
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
    },
    employeeMetaDate(employee) {
      if (employee.employee_type === 'staff') {
        return this.formatDate(employee.employment_date)
      }
      const start = this.formatDate(employee.term_start)
      const end = this.formatDate(employee.term_end)
      if (start === '—' && end === '—') return '—'
      return `${start} → ${end}`
    },
    employeeStatus(employee) {
      if (employee.employee_type === 'staff') {
        return employee.status || '—'
      }
      return employee.term_status || employee.status || '—'
    },
    statusBadgeVariant(status) {
      if (status === 'Inactive') return 'light-danger'
      if (status === 'Active' || status === 'Serving') return 'light-success'
      if (status === 'On leave') return 'light-warning'
      return 'secondary'
    },
  },
}
</script>

<style scoped>
.emp-stat {
  display: flex;
  gap: 0.85rem;
  align-items: center;
  padding: 1rem 1.1rem;
  border: 1px solid #e4ebe6;
  border-radius: 1rem;
  background: linear-gradient(180deg, #f7fbf8 0%, #ffffff 55%);
  height: 100%;
}

.emp-stat__icon {
  width: 46px;
  height: 46px;
  border-radius: 14px;
  background: #dff3e6;
  color: #04c431;
  display: grid;
  place-items: center;
  flex: 0 0 auto;
}

.emp-stat small {
  display: block;
  color: #6b7c72;
  font-size: 0.8rem;
}

.emp-stat h4 {
  color: #04c431;
  font-weight: 700;
}

.emp-stat__hint {
  color: #8a9a91;
  font-size: 0.75rem;
}

.employees-toolbar {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.25rem;
  flex-wrap: wrap;
}

.employees-toolbar__title {
  flex: 0 0 auto;
  color: #04c431;
}

.employees-toolbar__actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1 1 auto;
  justify-content: flex-end;
  min-width: 0;
}

.employees-toolbar__filter {
  width: 160px;
  flex: 0 0 auto;
}

.employees-toolbar__search {
  width: 100%;
  max-width: 240px;
  min-width: 160px;
}

.employees-toolbar__add {
  flex: 0 0 auto;
  white-space: nowrap;
}

.employees-toolbar__add >>> .btn,
.employees-toolbar__add >>> div {
  white-space: nowrap;
}

.employees-empty {
  border: 1px dashed rgba(0, 0, 0, 0.12);
  border-radius: 0.5rem;
  background: rgba(0, 0, 0, 0.02);
}

.employees-empty__icon {
  color: #04c431;
  margin-bottom: 0.35rem;
}

.cursor-pointer {
  cursor: pointer;
}

.person-card {
  display: flex;
  flex-direction: column;
  padding: 1.1rem;
  border: 1px solid #e4ebe6;
  border-radius: 1rem;
  background: linear-gradient(180deg, #f7fbf8 0%, #ffffff 42%);
  box-shadow: 0 1px 2px rgba(4, 196, 49, 0.04);
  transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;
}

.person-card:hover {
  transform: translateY(-2px);
  border-color: #c9ddd2;
  box-shadow: 0 0.5rem 1.25rem rgba(4, 196, 49, 0.1);
}

.person-card__head {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 0.95rem;
}

.person-card__avatar {
  width: 46px;
  height: 46px;
  border-radius: 14px;
  background: #04c431;
  color: #fff;
  display: grid;
  place-items: center;
  font-weight: 700;
  font-size: 0.9rem;
  flex: 0 0 auto;
}

.person-card__identity {
  flex: 1 1 auto;
  min-width: 0;
}

.person-card__identity b {
  display: block;
  color: #04c431;
  font-size: 1rem;
  line-height: 1.25;
}

.person-card__role {
  color: #6b7c72;
  font-size: 0.86rem;
  margin-top: 0.15rem;
}

.person-card__badge {
  display: inline-flex;
  align-items: center;
  padding: 0.28rem 0.65rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  white-space: nowrap;
  flex: 0 0 auto;
}

.person-card__badge.is-staff {
  background: #dbeafe;
  color: #1e40af;
}

.person-card__badge.is-executive {
  background: #FEF3C7;
  color: #B45309;
}

.person-card__meta {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.65rem;
}

.person-card__meta > div {
  background: #f6f8f6;
  border-radius: 0.7rem;
  padding: 0.6rem 0.7rem;
}

.person-card__meta small {
  display: block;
  color: #7a8a80;
  font-size: 0.7rem;
  margin-bottom: 0.15rem;
}

.person-card__meta b {
  display: block;
  color: #04c431;
  font-size: 0.86rem;
  font-weight: 700;
  line-height: 1.25;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.person-card__status-badge {
  display: inline-flex;
  margin-top: 0.1rem;
  font-size: 0.75rem;
  font-weight: 700;
}

@media (max-width: 768px) {
  .employees-toolbar__actions {
    width: 100%;
    flex-wrap: wrap;
  }

  .employees-toolbar__filter,
  .employees-toolbar__search {
    max-width: none;
    flex: 1 1 auto;
  }
}
</style>
